import { Check, Copy, ExternalLink, Link2, Plus, Trash2, TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Modal } from '@/components/ui/Modal'
import { Skeleton } from '@/components/ui/Skeleton'
import { useAuth } from '@/lib/auth'
import {
  useCreateOAuthClient,
  useOAuthClients,
  useReactivateOAuthClient,
  useRevokeOAuthClient,
} from '@/lib/oauth-api'
import type { CreateOAuthClientResult, McpScope, OAuthClientType, OAuthClientListItem } from '@/lib/oauth-types'
import { cn } from '@/lib/utils'
import { formatDate } from './shared'

const CODEX_REDIRECT_URI = 'http://127.0.0.1/callback'

const CLAUDE_REDIRECT_URI = 'https://claude.ai/api/mcp/auth_callback'

/** Spiegelt App\Enum\McpScope; de teksten staan daar in label() en description(). */
const MCP_SCOPES: { value: McpScope; label: string; description: string }[] = [
  { value: 'full', label: 'Volledig', description: 'Alles wat jij zelf in de app mag. Een koppeling krijgt nooit meer rechten dan je rol je geeft.' },
  { value: 'hours', label: 'Uren en lezen', description: 'Opvragen, plus uren, reiskosten, vakantie en weken bijwerken. Raakt geen facturen.' },
  { value: 'read', label: 'Alleen lezen', description: 'Alleen opvragen en meekijken. De koppeling kan niets wijzigen.' },
]

export function ConnectionsTab() {
  const auth = useAuth()
  const role = auth.data?.user.role
  const clientsQuery = useOAuthClients()
  const revoke = useRevokeOAuthClient()
  const reactivate = useReactivateOAuthClient()
  const [createOpen, setCreateOpen] = useState(false)
  const clients = clientsQuery.data ?? []

  const abilities =
    role === 'ADMIN'
      ? ['uren invoeren en bijwerken', 'facturen opstellen, goedkeuren en versturen', 'rapportages en dashboards draaien']
      : role === 'PARTNER'
        ? ['je eigen uren en reiskosten invoeren en bijwerken', 'facturen, uitbetalingen en rapportages inzien']
        : role === 'CONTRACTOR'
          ? ['je eigen uren en reiskosten invoeren en bijwerken', 'je eigen uitbetalingen inzien']
          : ['je eigen uren invoeren en bijwerken', 'je eigen overzichten inzien']

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Een AI-assistent koppelen"
          subtitle="Verbind Codex, Claude of een andere geschikte MCP-client"
          action={
            <a
              href="/api/doc"
              target="_blank"
              rel="noreferrer"
              className="btn-secondary h-8 px-2.5 text-sm"
            >
              API-documentatie
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          }
        />

        <p className="mt-4 max-w-2xl text-sm text-ink-soft">
          Een koppeling geeft een AI-assistent, zoals Codex of Claude, namens jou toegang tot dit systeem. Je geeft
          je week in één zin door in plaats van dag voor dag, je stuurt een inkoopfactuur door zodra je hem binnenkrijgt
          en de assistent zet hem bij de juiste uitbetaling, en je vraagt om een analyse die verder gaat dan de
          rapportages hier, zoals de uren per klant naast die van vorig jaar. Kies hieronder de uitleg voor
          jouw client. Welke acties beschikbaar zijn, hangt af van de rechten die je de koppeling geeft.
        </p>

        <div className="mt-6">
          <p className="label-caps">Wat kan de assistent ermee</p>
          <ul className="mt-2.5 space-y-1.5">
            {abilities.map((ability) => (
              <li key={ability} className="flex items-start gap-2.5 text-sm text-ink-soft">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-600" />
                {ability}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 max-w-2xl">
          <p className="label-caps">Server-URL</p>
          <CodeBlock code={`${window.location.origin}/mcp`} />
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Codex op je computer" subtitle="Inloggen via je browser, zonder client-secret" />
          <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-ink-soft">
            <li>Maak hieronder een koppeling van het type Codex. Kies welke gegevens en acties je nodig hebt.</li>
            <li>Laat de lokale callback staan en kopieer na het aanmaken het getoonde terminalcommando.</li>
            <li>Voer het commando uit op de computer waarop Codex draait. Log in bij Finance en controleer de rechten voordat je toestemming geeft.</li>
            <li>Herstart Codex en controleer de verbinding met <code>/mcp</code>.</li>
          </ol>
          <p className="mt-3 text-xs text-ink-faint">Gebruik een actuele Codex-versie. Deze stappen gelden voor de lokale app, CLI en IDE-extensie.</p>
        </Card>
        <Card>
          <CardHeader title="Claude of een andere client met secret" subtitle="Koppelen met een client-ID en client-secret" />
          <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-ink-soft">
            <li>Maak hieronder een koppeling van het type Client met secret.</li>
            <li>Vul de callback-URL van je client in. Voor Claude kun je de voorgestelde URL gebruiken.</li>
            <li>Voeg in je client een aangepaste MCP-connector toe met de server-URL hierboven. Vul bij de OAuth-instellingen het client-ID en secret in.</li>
            <li>Log in bij Finance en controleer de rechten voordat je toestemming geeft.</li>
          </ol>
          <p className="mt-3 text-xs text-ink-faint">De client moet OAuth met PKCE (S256) ondersteunen. Het secret wordt alleen bij het aanmaken getoond.</p>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Jouw koppelingen"
          subtitle="Elke koppeling heeft een eigen client-ID en gekozen rechten"
          action={
            <button type="button" className="btn-secondary h-8 px-2.5 text-sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Nieuwe koppeling toevoegen
            </button>
          }
        />
        {clientsQuery.isError ? (
          <ErrorState compact error={clientsQuery.error} onRetry={() => clientsQuery.refetch()} />
        ) : clientsQuery.isLoading ? (
          <Skeleton className="mt-4 h-24 w-full" />
        ) : clients.length === 0 ? (
          <EmptyState
            className="mt-4"
            icon={Link2}
            title="Nog geen koppelingen"
            description="Maak een koppeling aan om een AI-assistent te verbinden."
          />
        ) : (
          <div className="mt-4">
            {clients.map((client) => (
              <OAuthClientRow
                key={client.id}
                client={client}
                onRevoke={() => revoke.mutate(client.id)}
                onReactivate={() => reactivate.mutate(client.id)}
              />
            ))}
          </div>
        )}
      </Card>

      <div className="flex gap-3 rounded-lg border border-hairline bg-fill-1 px-4 py-3.5">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-status-approval" strokeWidth={1.85} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink">Behandel een koppeling als een wachtwoord</p>
          <ul className="mt-2 space-y-1.5 text-sm text-ink-soft">
            <li>Een verbonden assistent handelt namens jou, binnen de gekozen rechten en je eigen rol.</li>
            <li>Deel secrets en toegangstokens nooit met anderen. Plak ze ook niet in een chat.</li>
            <li>Herken je een koppeling niet meer? Verwijder hem hier direct.</li>
            <li>Codex gebruikt geen secret. Je login, toestemming en PKCE beveiligen de verbinding.</li>
            <li>Gebruik je een koppeling langere tijd niet, dan wordt hij automatisch uitgeschakeld.</li>
          </ul>
        </div>
      </div>

      {createOpen ? <CreateOAuthClientModal onClose={() => setCreateOpen(false)} /> : null}
    </div>
  )
}

function OAuthClientRow({ client, onRevoke, onReactivate }: { client: OAuthClientListItem; onRevoke: () => void; onReactivate: () => void }) {
  return (
    <div className="border-t border-hairline py-3 first:border-t-0">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{client.name}</p>
          <p className="mt-0.5 truncate text-xs text-ink-faint">
            {client.clientType === 'codex' ? 'Codex' : 'Client met secret'}
            {' · '}
            {client.mcpScopeLabel}
            {' · '}
            aangemaakt {formatDate(client.createdAt)}
            {' · '}
            {client.lastUsedAt ? `laatst actief ${formatDate(client.lastUsedAt)}` : 'nog niet gebruikt'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={cn(
              'flex items-center gap-1.5 rounded-md bg-fill-1 px-2 py-1 text-xs text-ink-soft ring-1 ring-hairline',
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', client.active ? 'bg-accent-600' : 'bg-fill-3')} />
            {client.active ? (client.connected ? 'Actief · verbonden' : 'Actief') : 'Uitgeschakeld'}
          </span>
          {client.active ? (
            <RevokeButton onConfirm={onRevoke} />
          ) : (
            <button type="button" className="btn-ghost h-8 px-2.5 text-xs font-medium" onClick={onReactivate}>
              Inschakelen
            </button>
          )}
        </div>
      </div>
      <details className="mt-2 text-sm text-ink-soft">
        <summary className="cursor-pointer">Verbindingsinstructies</summary>
        <ConnectionInstructions client={client} />
      </details>
    </div>
  )
}

function ConnectionInstructions({ client }: { client: OAuthClientListItem }) {
  const serverUrl = `${window.location.origin}/mcp`
  if (client.clientType === 'codex') {
    return (
      <div className="mt-3 space-y-3">
        <p className="text-sm text-ink-soft">Voer dit uit in je terminal. Codex opent de browser om in te loggen en toestemming te geven.</p>
        <CodeBlock code={`codex mcp add cw-finance --url '${serverUrl}' --oauth-client-id '${client.id}'`} />
        <p className="text-xs text-ink-faint">De callback die Codex toont moet overeenkomen met {client.redirectUri}. Een wisselende lokale poort is toegestaan. Een afwijkend pad niet: maak daarvoor een nieuwe koppeling met de exacte callback.</p>
        <p className="text-sm text-ink-soft">Opnieuw inloggen kan met:</p>
        <CodeBlock code="codex mcp login cw-finance --scopes mcp" />
        <p className="text-xs text-ink-faint">Herstart Codex na het koppelen. Gebruik een andere servernaam dan cw-finance als je meerdere koppelingen wilt bewaren.</p>
      </div>
    )
  }
  return (
    <div className="mt-3 space-y-3">
      <p className="text-sm text-ink-soft">Voeg een aangepaste MCP-connector toe in je client. Vul de server-URL in en geef bij de OAuth-instellingen het client-ID en je bewaarde secret op.</p>
      <p className="label-caps">Server-URL</p>
      <CodeBlock code={serverUrl} />
      <p className="label-caps">Client-ID</p>
      <CodeBlock code={client.id} />
      <p className="text-xs text-ink-faint">Callback: {client.redirectUri}. Secret kwijt? Verwijder de koppeling en maak een nieuwe aan.</p>
    </div>
  )
}

function RevokeButton({ onConfirm }: { onConfirm: () => void }) {
  const [armed, setArmed] = useState(false)

  useEffect(() => {
    if (!armed) return
    const timer = setTimeout(() => setArmed(false), 3000)
    return () => clearTimeout(timer)
  }, [armed])

  if (armed) {
    return (
      <button type="button" className="btn-ghost h-8 shrink-0 px-2.5 text-xs font-medium text-status-overdue" onClick={onConfirm}>
        Verwijderen?
      </button>
    )
  }

  return (
    <button type="button" className="btn-ghost h-8 w-8 shrink-0 p-0 text-status-overdue" onClick={() => setArmed(true)} aria-label="Verwijderen">
      <Trash2 className="h-4 w-4" />
    </button>
  )
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-hairline bg-surface-2">
      <div className="flex items-center justify-end border-b border-hairline px-2 py-1.5">
        <button
          type="button"
          className="btn-ghost h-6 px-2 text-xs"
          onClick={() => { navigator.clipboard?.writeText(code); setCopied(true) }}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-status-paid" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Gekopieerd' : 'Kopieer'}
        </button>
      </div>
      <pre className="overflow-x-auto px-3 py-2.5 text-xs leading-relaxed">
        <code className="font-mono text-ink">{code}</code>
      </pre>
    </div>
  )
}

function CreateOAuthClientModal({ onClose }: { onClose: () => void }) {
  const create = useCreateOAuthClient()
  const [name, setName] = useState('')
  const [clientType, setClientType] = useState<OAuthClientType>('codex')
  const [redirectUri, setRedirectUri] = useState(CODEX_REDIRECT_URI)
  const [mcpScope, setMcpScope] = useState<McpScope>('read')
  const [result, setResult] = useState<CreateOAuthClientResult | null>(null)
  const [copiedSecret, setCopiedSecret] = useState(false)

  useEffect(() => {
    if (!copiedSecret) return
    const timer = setTimeout(() => setCopiedSecret(false), 2000)
    return () => clearTimeout(timer)
  }, [copiedSecret])

  function submit() {
    if (!name.trim() || !redirectUri.trim()) return
    create.mutate({ name: name.trim(), redirectUri: redirectUri.trim(), mcpScope, clientType }, { onSuccess: setResult })
  }

  function copySecret() {
    if (result?.clientSecret) {
      navigator.clipboard?.writeText(result.clientSecret)
      setCopiedSecret(true)
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={result ? 'Koppeling aangemaakt' : 'Nieuwe koppeling'}
      description={result ? (result.clientType === 'codex' ? 'Verbind Codex met het commando hieronder. Er is geen secret nodig.' : 'Kopieer het client-ID en de secret nu; de secret wordt daarna niet meer getoond.') : 'Kies je client en de rechten die de assistent nodig heeft.'}
      footer={
        result ? (
          <button type="button" className="btn-primary" onClick={onClose}>Klaar</button>
        ) : (
          <>
            <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
            <button type="button" className="btn-primary" onClick={submit} disabled={!name.trim() || !redirectUri.trim() || create.isPending}>Aanmaken</button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-4">
          <div>
            <p className="label-caps">Client ID</p>
            <CodeBlock code={result.id} />
          </div>
          {result.clientSecret !== null && <div>
            <p className="label-caps">Client secret</p>
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-hairline bg-fill-1 px-3 py-2.5">
              <code className="num flex-1 truncate text-sm text-ink">{result.clientSecret}</code>
              <button type="button" className="btn-secondary h-8 px-2.5 text-sm" onClick={copySecret}>
                {copiedSecret ? <Check className="h-4 w-4 text-status-paid" /> : <Copy className="h-4 w-4" />}
                {copiedSecret ? 'Gekopieerd' : 'Kopieer'}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-ink-faint">Bewaar deze secret veilig. Je kunt hem niet opnieuw inzien.</p>
          </div>}
          <ConnectionInstructions client={result} />
        </div>
      ) : (
        <div className="space-y-3">
          <fieldset className="space-y-2">
            <legend className="mb-1.5 text-sm font-medium text-ink-soft">Type koppeling</legend>
            {([
              { value: 'codex', label: 'Codex', description: 'Lokale app, CLI of IDE. Login via de browser, zonder secret.' },
              { value: 'confidential', label: 'Client met secret', description: 'Bijvoorbeeld Claude. Je krijgt een client-ID en secret.' },
            ] as const).map((option) => (
              <label key={option.value} className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-hairline px-3 py-2.5">
                <input type="radio" name="client-type" className="mt-0.5" aria-labelledby={`client-type-${option.value}-label`} aria-describedby={`client-type-${option.value}-description`} checked={clientType === option.value} onChange={() => {
                  setClientType(option.value)
                  setRedirectUri(option.value === 'codex' ? CODEX_REDIRECT_URI : CLAUDE_REDIRECT_URI)
                }} />
                <span>
                  <span id={`client-type-${option.value}-label`} className="block text-sm font-medium text-ink">{option.label}</span>
                  <span id={`client-type-${option.value}-description`} className="block text-xs text-ink-faint">{option.description}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Naam</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className="field" placeholder="Bijv. Codex - laptop" autoFocus />
          </label>
          <p className="text-xs text-ink-faint">Kies een naam die je later helpt herkennen waar deze koppeling voor is.</p>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Redirect-URI</span>
            <input value={redirectUri} onChange={(e) => setRedirectUri(e.target.value)} className="field" placeholder="https://..." />
          </label>
          <div className="pt-1">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Wat mag deze koppeling</span>
            <div className="space-y-1.5">
              {MCP_SCOPES.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-hairline px-3 py-2.5 hover:bg-fill-1"
                >
                  <input
                    type="radio"
                    name="mcp-scope"
                    className="mt-0.5"
                    checked={mcpScope === option.value}
                    onChange={() => setMcpScope(option.value)}
                  />
                  <span>
                    <span className="block text-sm font-medium text-ink">{option.label}</span>
                    <span className="block text-xs text-ink-faint">{option.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-ink-faint">
              Wat buiten de keuze valt bestaat voor de koppeling niet: die tools staan niet in de lijst die de
              assistent krijgt, dus hij kan ze ook niet per ongeluk aanroepen.
            </p>
          </div>
          {clientType === 'codex' ? (
            <p className="text-xs text-ink-faint">Laat de lokale callback staan voor een nieuwe Codex-koppeling. Gebruik je een aangepaste callback, neem dan exact de URL over die Codex toont. Alleen http://127.0.0.1 met het Codex-callbackpad is toegestaan.</p>
          ) : <p className="text-xs text-ink-faint">
            Voor Claude is dit{' '}
            <button type="button" className="font-medium text-accent-700 underline hover:text-accent-600" onClick={() => setRedirectUri(CLAUDE_REDIRECT_URI)}>
              {CLAUDE_REDIRECT_URI}
            </button>
            . Gebruik je een andere tool, kopieer dan de callback-URL die dat tool zelf toont bij het toevoegen van
            een aangepaste connector.
          </p>}
        </div>
      )}
    </Modal>
  )
}
